import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Crown, Trophy, Flame, Calendar, Zap, LogIn, ChevronsDown, Star,
} from "lucide-react";
import { useAuth } from "../components/AuthProvider";

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { id: "daily",    label: "Today",    icon: Flame },
  { id: "weekly",   label: "Week",     icon: Zap },
  { id: "monthly",  label: "Month",    icon: Calendar },
  { id: "lifetime", label: "Lifetime", icon: Crown },
];

const SCOPE_METRIC_LABEL = {
  daily: "Daily Score",
  weekly: "Weekly Points",
  monthly: "Monthly Points",
  lifetime: "CSS",
};

export default function DailyLeaderboard() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [scope, setScope] = useState("daily");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    fetch(`${API}/api/daily/leaderboard?scope=${scope}&limit=50`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => alive && setData(d))
      .catch(() => alive && setErr("Couldn't load leaderboard."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [scope]);

  const rows = data?.rows || [];
  const me = data?.me;
  const inList = useMemo(() => me && rows.some((r) => r.user_id === me.user_id), [me, rows]);

  const jumpToMe = () => {
    const el = document.querySelector("[data-testid='dl-my-row']");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
        <button data-testid="dl-back-btn" onClick={() => nav(-1)}
          className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">
              <Trophy size={12} /> Daily Smash Leaderboard
            </div>
            <h1 className="font-display text-5xl leading-[0.9] tracking-tight mt-1">
              Smash Kings & Queens
            </h1>
          </div>
          {me && inList && (
            <button data-testid="dl-jump-btn" onClick={jumpToMe}
              className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#00E5FF] border border-[#00E5FF]/40 rounded-full px-3 py-1.5 hover:bg-[#00E5FF]/10">
              <ChevronsDown size={12} className="inline mr-1" /> Me
            </button>
          )}
        </div>

        {/* Scope tabs */}
        <div data-testid="dl-scope-tabs"
          className="mt-5 rounded-full border border-white/10 bg-black/30 p-1 flex gap-1">
          {SCOPES.map((s) => (
            <button key={s.id} data-testid={`dl-tab-${s.id}`} onClick={() => setScope(s.id)}
              className={`flex-1 rounded-full py-2 text-[10px] tracking-[0.2em] uppercase font-bold flex items-center justify-center gap-1 transition-colors ${
                scope === s.id ? "bg-[#FF107A] text-[#FDFBF7]" : "text-[#FDFBF7]/60"
              }`}>
              <s.icon size={11} /> {s.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={scope} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}>
            {loading && <div className="mt-8 text-center text-[#FDFBF7]/60 text-sm">Loading rankings…</div>}
            {err && !loading && <div className="mt-8 text-center text-[#FF107A] text-sm" data-testid="dl-error">{err}</div>}

            {!loading && !err && rows.length === 0 && (
              <div className="mt-10 rounded-2xl border border-white/10 bg-black/30 p-8 text-center">
                <Star className="mx-auto mb-3 text-[#A855F7]" />
                <div className="font-display text-2xl">Nobody yet</div>
                <div className="text-sm text-[#FDFBF7]/60 mt-1">
                  {scope === "daily" ? "Be the first to play today's Smash." : "No plays in this window yet."}
                </div>
                <button data-testid="dl-empty-play-btn" onClick={() => nav("/daily")}
                  className="mt-4 rounded-full bg-[#FF107A] text-[#FDFBF7] px-5 py-2 font-bold">
                  Play Now
                </button>
              </div>
            )}

            {rows.length > 0 && (
              <div className="mt-6 space-y-2" data-testid="dl-rows">
                {rows.map((r) => (
                  <Row key={r.user_id} row={r} scope={scope} isMe={me && me.user_id === r.user_id} />
                ))}
              </div>
            )}

            {me && !inList && !loading && (
              <div className="mt-8">
                <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#00E5FF] mb-2">Your Position</div>
                <Row row={me} scope={scope} isMe />
              </div>
            )}

            {!user && !loading && (
              <button data-testid="dl-signin-btn" onClick={login}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide">
                <LogIn size={16} /> Sign in to see your rank
              </button>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-6 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/40 text-center">
          {scope === "daily" && "Resets every 24 hours"}
          {scope === "weekly" && "Rolling 7-day window"}
          {scope === "monthly" && "Rolling 30-day window"}
          {scope === "lifetime" && "All-time Celebrity Smash Score"}
        </div>
      </div>
    </div>
  );
}

const RANK_TINT = { 1: "#FFD700", 2: "#C0C7D2", 3: "#E58A4B" };

const Row = ({ row, scope, isMe }) => {
  const tint = RANK_TINT[row.rank];
  const primary = scope === "lifetime" ? row.css :
                  scope === "monthly" ? row.monthly_score :
                  scope === "weekly" ? row.weekly_score : row.daily_score;
  const secondary =
    scope === "daily"    ? `Streak ${row.streak || 0} · ${fmt(row.css)} CSS` :
    scope === "weekly"   ? `${row.plays || 0} plays · ${row.prediction_accuracy || 0}% pred acc` :
    scope === "monthly"  ? `${row.perfect_matches || 0} perfect · ${row.plays || 0} plays` :
                           `${row.total_daily_smashes || 0} smashes · ${row.perfect_matches || 0} perfect`;

  return (
    <div
      data-testid={isMe ? "dl-my-row" : `dl-row-${row.rank}`}
      className={`rounded-2xl border px-3 py-2.5 flex items-center gap-3 ${
        isMe ? "border-[#00E5FF]/60 bg-[#00E5FF]/10 shadow-[0_0_18px_rgba(0,229,255,0.35)]"
             : "border-white/10 bg-[#16141D]/80"
      }`}
    >
      <div className="w-8 text-center font-display text-2xl tabular leading-none"
        style={{ color: tint || (isMe ? "#00E5FF" : "rgba(253,251,247,0.7)") }}>
        {row.rank}
      </div>
      <Avatar user={row} size={40} ring={isMe ? "#00E5FF" : (tint || "rgba(255,255,255,0.15)")} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-[#FDFBF7] truncate">{row.name}</span>
          {isMe && <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-[#00E5FF]">You</span>}
        </div>
        <div className="text-[10px] tracking-[0.1em] uppercase text-[#FDFBF7]/60 truncate">
          <span className="text-[#FFD700] font-bold">{row.title}</span> · {secondary}
        </div>
      </div>
      <div className="text-right">
        <div className="font-display text-xl tabular leading-none text-[#FF107A]">
          {fmt(primary)}
        </div>
        <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/50 mt-0.5">
          {SCOPE_METRIC_LABEL[scope]}
        </div>
      </div>
    </div>
  );
};

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : (n ?? "—"));

const Avatar = ({ user, size = 40, ring = "rgba(255,255,255,0.15)" }) => {
  const initial = (user?.name || "?").slice(0, 1).toUpperCase();
  return (
    <div className="rounded-2xl overflow-hidden flex items-center justify-center font-display shrink-0"
      style={{ width: size, height: size, border: `2px solid ${ring}`, background: "#0A090D" }}>
      {user?.picture ? (
        <img src={user.picture} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="text-[#FF107A]" style={{ fontSize: size * 0.5 }}>{initial}</span>
      )}
    </div>
  );
};
